import type { FormaPago, Job, JobStatus, JobCompletionChecklist } from '@conversia/shared-types';
import type { Paginated } from '@/lib/types';
import { api } from './client';
import { useAuthStore } from '@/store/auth.store';
import { jobsExportQuery } from '@/lib/jobs-export-query';

/** Endpoints de trabajos/servicios (ADMIN). Prefijo: /api/jobs */

/** Job con las relaciones que devuelve la API (contacto + técnico resumidos). */
export interface JobWithRelations extends Job {
  contact?: { id: string; name: string | null; pushName: string | null; phone: string | null } | null;
  employee?: { id: string; nombre: string; fotoUrl: string | null; rol: string } | null;
}

/**
 * S-06 · Descarga el Excel de Trabajos con los MISMOS filtros de la pantalla.
 *
 * NO se puede usar un `<a href>` plano: la ruta exige `Authorization` y una
 * navegación del browser no lleva ese header — daría 401. Mismo patrón que
 * `downloadCrmExport`. El nombre del archivo lo pone el servidor (sufijo
 * PARCIAL si el tope de filas recortó el resultado).
 */
export async function downloadJobsExport(params: ListJobsParams = {}): Promise<void> {
  const base = (import.meta.env.VITE_API_URL as string | undefined) ?? '';
  const token = useAuthStore.getState().accessToken;
  const qs = jobsExportQuery(params as Record<string, unknown>);
  const res = await fetch(`${base}/api/jobs/export.xlsx${qs ? `?${qs}` : ''}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
  });
  if (!res.ok) throw new Error('No se pudo generar el Excel');
  const disposition = res.headers.get('content-disposition') ?? '';
  const nombre = /filename="([^"]+)"/.exec(disposition)?.[1] ?? 'trabajos.xlsx';
  const url = URL.createObjectURL(await res.blob());
  const a = document.createElement('a');
  a.href = url;
  a.download = nombre;
  a.click();
  URL.revokeObjectURL(url);
}

export interface ListJobsParams {
  estado?: JobStatus;
  /** S-10 · Solo abiertos con el plazo vencido (mismo conjunto que Tracking). */
  vencidos?: boolean;
  /** S-04 · true = solo casos remotos; false = solo visitas de campo. */
  esRemoto?: boolean;
  /** S-05 · Solo los cierres que quedaron fuera del radio del destino. */
  fueraDeRadio?: boolean;
  /** Registros de PRUEBA. Sin especificar se excluyen; `true` muestra solo ellos. */
  esPrueba?: boolean;
  employeeId?: string;
  contactId?: string;
  /** Búsqueda libre por cliente, dirección o descripción. */
  buscar?: string;
  /** Orden de la lista. Por defecto, la cita más próxima primero. */
  orden?: 'cita_proxima' | 'cita_lejana' | 'recientes';
  /** Rango de citas (ISO absoluto); un trabajo sin cita queda fuera del rango. */
  from?: string;
  to?: string;
  skip?: number;
  take?: number;
}

export const listJobs = (params: ListJobsParams = {}) =>
  api.get<Paginated<JobWithRelations>>('/api/jobs', { query: params });

export const getJob = (id: string) => api.get<JobWithRelations>(`/api/jobs/${id}`);

export const createJob = (input: {
  /** Registro de PRUEBA: queda fuera de reportes y del panel de ventas. */
  esPrueba?: boolean;
  contactId: string;
  conversationId?: string | null;
  employeeId?: string | null;
  descripcion: string;
  direccion?: string | null;
  zona?: string | null;
  scheduledAt?: string | null;
  esGarantia?: boolean;
  targetLat?: number | null;
  targetLng?: number | null;
  geofenceRadiusM?: number | null;
  mapsUrl?: string | null;
}) => api.post<JobWithRelations>('/api/jobs', input);

/** Trabajos con cita en [from, to] (ISO) para la vista Despachos. */
export const listDespachos = (from: string, to: string) =>
  api.get<JobWithRelations[]>('/api/jobs/despachos', { query: { from, to } });

/** Corrección manual de la ficha de despacho de un trabajo. */
export const updateDespacho = (
  id: string,
  patch: {
    vendedor?: string | null;
    formaPago?: FormaPago | null;
    comprobanteFiscal?: 'si' | 'no' | null;
    rnc?: string | null;
    compania?: string | null;
    mapsUrl?: string | null;
  },
) => api.patch<JobWithRelations>(`/api/jobs/${id}/despacho`, patch);

/**
 * Edición completa post-creación: identidad/agenda del trabajo, sin tocar
 * estado ni ponches. En un trabajo cerrado el server solo acepta el registro
 * administrativo (descripción/dirección/zona/notas).
 */
export const updateJob = (
  id: string,
  patch: {
    descripcion?: string;
    direccion?: string | null;
    zona?: string | null;
    notas?: string | null;
    scheduledAt?: string | null;
    employeeId?: string | null;
    targetLat?: number | null;
    targetLng?: number | null;
    geofenceRadiusM?: number | null;
    mapsUrl?: string | null;
  },
) => api.patch<JobWithRelations>(`/api/jobs/${id}`, patch);

export const assignEmployee = (id: string, employeeId: string | null) =>
  api.post<JobWithRelations>(`/api/jobs/${id}/assign`, { employeeId });

/** Borra un trabajo. `force` confirma borrar un COMPLETADO con encuesta (409 sin él). */
export const deleteJob = (id: string, force = false) =>
  api.del<{ deleted: boolean }>(`/api/jobs/${id}`, force ? { query: { force: 'true' } } : undefined);

export const setWarranty = (id: string, esGarantia: boolean) =>
  api.post<JobWithRelations>(`/api/jobs/${id}/warranty`, { esGarantia });

export const setJobTarget = (
  id: string,
  target: { targetLat: number | null; targetLng: number | null; geofenceRadiusM: number | null },
) => api.post<JobWithRelations>(`/api/jobs/${id}/target`, target);

export const changeStatus = (
  id: string,
  input: { estado: JobStatus; resultado?: string | null; motivo?: string | null },
) => api.post<JobWithRelations>(`/api/jobs/${id}/status`, input);

/** Cancela un trabajo abierto (Fase 2): decisión del admin, motivo obligatorio. */
export const cancelJob = (id: string, motivo: string) =>
  api.post<JobWithRelations>(`/api/jobs/${id}/cancel`, { motivo });

/** Edita el reporte de cierre de un trabajo cerrado (Fase 2, solo ADMIN). */
export const updateCierre = (
  id: string,
  patch: {
    resultado?: string | null;
    motivo?: string | null;
    checklist?: JobCompletionChecklist | null;
    registroArticulo?: string | null;
  },
) => api.patch<JobWithRelations>(`/api/jobs/${id}/cierre`, patch);

/**
 * S-01 · VALIDACIÓN DE SUPERVISIÓN: cierra el trabajo en firme.
 *
 * El técnico ya cerró en sitio (modelo + serie + resultado) y el trabajo quedó
 * "Por validar". Acá se completa lo comercial. Las GARANTÍAS van sin monto,
 * forma de pago ni vendedor: no hay venta.
 */
export const validarCierre = (
  id: string,
  input: {
    montoCierre?: number | null;
    formaPago?: string | null;
    vendedorUserId?: string | null;
    rnc?: string | null;
    comprobanteFiscal?: string | null;
    /** S-02 · Corrección de la tipificación; si no van, se confirma la del técnico. */
    nivelResolucion?: string | null;
    causaRaiz?: string | null;
  },
) => api.post<JobWithRelations>(`/api/jobs/${id}/validar`, input);

/**
 * S-01 · Marca que el cliente pidió comprobante fiscal. Con esto marcado, la
 * validación exige RNC y número de comprobante.
 */
export const setComprobanteFiscal = (id: string, requiereComprobanteFiscal: boolean) =>
  api.post<JobWithRelations>(`/api/jobs/${id}/comprobante-fiscal`, {
    requiereComprobanteFiscal,
  });

/** S-03 · Una instalación del cliente a la que se le puede enlazar una garantía. */
export interface InstalacionCandidata {
  id: string;
  descripcion: string;
  modelo: string | null;
  numeroSerie: string | null;
  completedAt: string | null;
  tecnicoNombre: string | null;
}

/** Instalaciones cerradas del cliente (para elegir el origen de la garantía). */
export const listInstalaciones = (contactId: string) =>
  api.get<InstalacionCandidata[]>(
    `/api/jobs/instalaciones?contactId=${encodeURIComponent(contactId)}`,
  );

/**
 * S-03 · Abre una garantía. Tres caminos: enlazada (hereda todo de la
 * instalación), previa al sistema (modelo, serie y fecha a mano) o SUELTA (la
 * artículo no está en el sistema y esos tres datos son opcionales).
 */
export const crearGarantia = (input: {
  instalacionJobId?: string | null;
  instalacionPrevia?: boolean;
  /** El artículo no está en el sistema: modelo, serie y fecha son opcionales. */
  garantiaSuelta?: boolean;
  contactId?: string | null;
  descripcion: string;
  employeeId?: string | null;
  scheduledAt?: string | null;
  modelo?: string | null;
  numeroSerie?: string | null;
  fechaInstalacion?: string | null;
  conversationId?: string | null;
  notas?: string | null;
  /** S-04 · Caso remoto: sin técnico ni cita. */
  esRemoto?: boolean;
  /** S-09 · El cliente no puede entrar a su vivienda → prioridad máxima. */
  clienteSinAcceso?: boolean;
  /** Registro de PRUEBA. Se hereda si la instalación de origen lo es. */
  esPrueba?: boolean;
}) => api.post<JobWithRelations>('/api/jobs/garantia', input);

/** S-12 · Una conversación del cliente, para elegir a cuál enlazar el trabajo. */
export interface ConversacionCandidata {
  id: string;
  leadStatus: string | null;
  status: string;
  lastMessageAt: string | null;
  createdAt: string;
}

export const listConversacionesDeContacto = (contactId: string) =>
  api.get<ConversacionCandidata[]>(
    `/api/jobs/conversaciones?contactId=${encodeURIComponent(contactId)}`,
  );

/** S-12 · Enlaza (o desenlaza con null) el trabajo a una conversación del CRM. */
export const vincularConversacion = (id: string, conversationId: string | null) =>
  api.post<JobWithRelations>(`/api/jobs/${id}/conversacion`, { conversationId });

/** Marca/desmarca un trabajo como registro de PRUEBA (arrastra sus garantías). */
export const setEsPrueba = (id: string, esPrueba: boolean) =>
  api.post<{ job: JobWithRelations; garantiasArrastradas: number }>(
    `/api/jobs/${id}/es-prueba`,
    { esPrueba },
  );

/** Cuántos registros de prueba hay (se muestra antes de ofrecer el borrado). */
export const contarRegistrosDePrueba = () =>
  api.get<{ total: number }>('/api/jobs/registros-de-prueba/conteo');

/** Borra TODOS los registros de prueba. Solo toca lo marcado. */
export const borrarRegistrosDePrueba = () =>
  api.del<{ borrados: number }>('/api/jobs/registros-de-prueba');

/** S-09 · Marca/desmarca "el cliente no puede entrar a su vivienda". */
export const setClienteSinAcceso = (id: string, clienteSinAcceso: boolean) =>
  api.post<JobWithRelations>(`/api/jobs/${id}/sin-acceso`, { clienteSinAcceso });

/** S-04 · Cierra un caso remoto desde el panel, con su tipificación. */
export const cerrarCasoRemoto = (
  id: string,
  input: { resultado: string; nivelResolucion?: string; causaRaiz?: string },
) => api.post<JobWithRelations>(`/api/jobs/${id}/cerrar-remoto`, input);

/** S-04 · Escala un caso remoto a visita de campo (mismo trabajo, mismo historial). */
export const escalarCasoRemoto = (
  id: string,
  input: { employeeId?: string | null; scheduledAt?: string | null },
) => api.post<JobWithRelations>(`/api/jobs/${id}/escalar`, input);

/** Una entrada del historial de la ficha (Fase 1): quién, cuándo y qué cambió. */
export interface JobHistoryItem {
  id: string;
  at: string;
  action: string;
  userName: string;
  changes: Record<string, { antes: unknown; despues: unknown }> | null;
  fields: string[];
}

export const getJobHistory = (id: string) =>
  api.get<JobHistoryItem[]>(`/api/jobs/${id}/history`);
